import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { routerRequestAction } from '@ngrx/router-store';
import { Action, ActionCreator } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { LocalStorageService } from 'ngx-webstorage';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { addAbsolutePathPrefix } from '@rx-applications/shared/utils';

import { RouterFacade } from '@rx-applications/shared/data-access';
import { AuthFacade } from '../../facade/auth.facade';
import { AuthBackend } from '../../../backend/auth.backend';
import { AuthLoggerService, TokenService } from '../../../services';
import {
  activateActions,
  changePasswordActions,
  handleIncorrectPortal,
  impersonate,
  localStorageActions,
  loginActions,
  logoutActions,
  registerActions,
  requestPasswordActions,
  resetPasswordActions,
  saveTokenDetails,
  setupMFAActions,
  smsChallengeActions,
  userSessionActions,
} from '../../actions/auth.actions';
import { Credentials, LegacyLoginResponse, LoginResponse, LoginResponseInterface, SignupInformation } from '../../../models';
import { AuthRoutes } from '../../../constants';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class AuthEffects implements OnInitEffects {
  constructor(
    private actions$: Actions,
    private authBackend: AuthBackend,
    private router: Router,
    private routerFacade: RouterFacade,
    private authLoggerService: AuthLoggerService,
    private tokenService: TokenService,
    private localStorageService: LocalStorageService,
    private cookieService: CookieService,
    private authFacade: AuthFacade,
  ) {}

  loadTokenFromLocaleStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(localStorageActions.loadToken),
      switchMap(() => this.tokenService.getValidToken()),
      switchMap((token: string | null) => [localStorageActions.loadTokenSucceed({ token })]),
    ),
  );

  impersonate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impersonate),
      withLatestFrom(this.routerFacade.params$),
      switchMap(([, params]: [Action, Params]) => {
        const body = {
          username: params['guid'],
          password: null,
        };
        const userType = 'Impersonation';
        this.authLoggerService.setProperties({ isImpersonated: true });
        this.tokenService.removeToken();
        this.localStorageService.clear();

        return this.authBackend.impersonate(body, userType).pipe(
          map((response: LegacyLoginResponse) => saveTokenDetails({ response })),
          catchError((error) => {
            return of(loginActions.failed({ error }));
          }),
        );
      }),
    ),
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginActions.login),
      map(({ credentials }) => credentials),
      withLatestFrom(this.routerFacade.params$),
      switchMap(([credentials, params]: [Credentials, Params]) => {
        const { body, userType } = this.checkBodyForImpersonation(credentials, params);
        return this.authBackend.login(body, userType).pipe(
          map((loginResponse: LoginResponseInterface) => this.handleLoginResponse(credentials, userType, loginResponse)),
          catchError((error) => {
            this.authLoggerService.loginFailed({ Username: credentials.username });
            return of(loginActions.failed({ error }));
          }),
        );
      }),
    ),
  );

  saveTokenDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveTokenDetails),
      tap(({ response }) => {
        this.tokenService.setToken(response);
      }),
      switchMap(() => this.tokenService.getValidToken()),
      switchMap((token) => [loginActions.succeed({ token })]),
    ),
  );

  loginSucceed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginActions.succeed),
        withLatestFrom(this.routerFacade.queryParams$),
        tap(([, queryParams]) => {
          if (queryParams['redirectUrl']) {
            this.router.navigateByUrl(queryParams['redirectUrl']);
            return;
          }
          this.router.navigate(['/']);
        }),
      ),
    { dispatch: false },
  );

  handleIncorrectPortal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(handleIncorrectPortal),
        switchMap(({ email }) =>
          this.authBackend.getDomain(email).pipe(
            tap((correctDomain) => {
              if (location.hostname.split('.')[0] !== correctDomain.split('.')[0]) {
                this.tokenService.removeToken();
                window.location.href = `//${correctDomain}`;
              }
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  requestPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestPasswordActions.request),
      map(({ email }) => email),
      switchMap((email: string) =>
        this.authBackend.requestPassword(email).pipe(
          map(() => requestPasswordActions.succeed()),
          catchError((error) => of(requestPasswordActions.failed({ error }))),
        ),
      ),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetPasswordActions.reset),
      withLatestFrom(this.routerFacade.queryParams$),
      switchMap(
        ([{ password, confirmationCode }, queryParams]: [
          {
            password: string;
            confirmationCode: string;
          },
          Params,
        ]) =>
          this.authBackend.resetPassword(password, confirmationCode, queryParams['email']).pipe(
            map(() => resetPasswordActions.succeed()),
            catchError(({ error: { Message } }: HttpErrorResponse) => {
              const regexTranslateTagOnly = /^\b\w+\b$/;
              return of(
                resetPasswordActions.failed({
                  error: { name: '', message: Message && regexTranslateTagOnly.test(Message) ? Message : 'failed' },
                }),
              );
            }),
          ),
      ),
    ),
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePasswordActions.change),
      switchMap(({ currentPassword, password }: { currentPassword: string; password: string }) =>
        this.authBackend.changePassword(currentPassword, password, this.tokenService.getAccessToken()).pipe(
          map(() => changePasswordActions.succeed()),
          catchError((error) => of(changePasswordActions.failed({ error }))),
        ),
      ),
    ),
  );

  changePasswordSucceed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePasswordActions.succeed),
      map(() => logoutActions.logout()),
    ),
  );

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(registerActions.register),
      map(({ signupInformation }) => signupInformation),
      switchMap((signupInformation: SignupInformation) =>
        this.authBackend.signup(signupInformation).pipe(
          map(() => registerActions.succeed()),
          catchError((error) =>
            of(
              registerActions.failed({
                error: error.error ? new Error(error.error.Message) : error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  activateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activateActions.activate),
      withLatestFrom(this.routerFacade.queryParams$),
      switchMap(([, queryParams]: [Action, Params]) =>
        this.authBackend.activateAccount(queryParams['i']).pipe(
          map(() => activateActions.succeed()),
          catchError((error) => of(activateActions.failed({ error }))),
        ),
      ),
    ),
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logoutActions.logout),
      tap(() => this.authLoggerService.endSession()),
      switchMap(() => [logoutActions.succeed({ redirectUrl: '' })]),
    ),
  );

  clearUserSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userSessionActions.clearsession),
      tap(() => this.authLoggerService.endSession()),
      switchMap(() => {
        this.clearSession();
        return [userSessionActions.clearsucceed()];
      }),
    ),
  );

  changePassRequired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePasswordActions.force),
      tap(() => this.authLoggerService.forceChangePassword({ email: this.tokenService.getEmail() })),
      switchMap(() => [userSessionActions.clearsession(), changePasswordActions.forcenavigate()]),
    ),
  );

  navigateToChangePassRequired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(changePasswordActions.forcenavigate),
        tap(() => this.router.navigate([addAbsolutePathPrefix(AuthRoutes.ForceResetPassword)])),
      ),
    { dispatch: false },
  );

  navigateToLoginPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logoutActions.succeed, logoutActions.authguard),
        tap(({ redirectUrl }) => {
          this.clearSession();
          if (redirectUrl && redirectUrl.length > 0) {
            const username = new URL(redirectUrl, window.location.origin).searchParams.get('username');
            return this.router.navigate([addAbsolutePathPrefix(AuthRoutes.Login)], {
              queryParams: { redirectUrl, ...(username && { username: username }) },
            });
          }
          return this.router.navigate([addAbsolutePathPrefix(AuthRoutes.Login)]);
        }),
      ),
    { dispatch: false },
  );

  captureLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(routerRequestAction),
        map(({ payload }) => payload.event.url),
        filter((url) => url.includes(AuthRoutes.Logout)),
        tap(() => this.authFacade.logout()),
      ),
    { dispatch: false },
  );

  ngrxOnInitEffects(): Action {
    return localStorageActions.loadToken();
  }

  private handleLoginResponse(
    credentials: Credentials,
    userType: string | undefined,
    loginResponse: LoginResponseInterface,
  ) {
    if (!userType) {
      this.authLoggerService.loginSuccess({ Username: credentials.username });
    }

    const { isSmsMfaRequired, isMfaSetupRequired } = loginResponse as LoginResponse;

    const mfaActionRequired: ActionCreator<string, any> = new Map<boolean, ActionCreator>([
      [isMfaSetupRequired, setupMFAActions.request],
      [isSmsMfaRequired, smsChallengeActions.request],
    ]).get(true);

    return mfaActionRequired
      ? mfaActionRequired({ credentials, loginResponse: loginResponse as LoginResponse })
      : saveTokenDetails({ response: loginResponse });
  }

  private checkBodyForImpersonation(
    credentials: Credentials,
    params: Params,
  ): { body: Credentials; userType: string | undefined } {
    const isImpersonation = !!params['guid'];
    const body = this.getLoginBody(isImpersonation, params, credentials);
    const userType = isImpersonation ? 'Impersonation' : undefined;

    this.authLoggerService.setProperties({ isImpersonated: isImpersonation });
    this.clearTokenStorage(isImpersonation);

    return { body, userType };
  }

  private getLoginBody(isImpersonation: boolean, params: Params, credentials: Credentials): Credentials {
    return isImpersonation
      ? {
          username: params['guid'],
          password: null,
        }
      : credentials;
  }

  private clearTokenStorage(isImpersonation: boolean): void {
    if (isImpersonation) {
      this.tokenService.removeToken();
      this.localStorageService.clear();
    }
  }

  private clearSession(): void {
    this.tokenService.removeToken();
    this.localStorageService.clear();
    this.#deleteCookiesExcept(/.*_version$/);
  }

  #deleteCookiesExcept(regex: RegExp) {
    const allCookies = this.cookieService.getAll();

    Object.keys(allCookies).forEach((cookieName) => {
      if (!regex.test(cookieName)) {
        this.cookieService.delete(cookieName);
      }
    });
  }
}
